import { SectionHeader } from "@/components/common/section-components/SectionHeader";
import { BackupCheckMainDataCard } from "@/components/common/section-components/card/BackupCheckMainDataCard";
import { BackupCheckAmountAndActions } from "@/components/common/section-components/requests/backup-check/BackupCheckAmountAndActions";
import { AppRoutes } from "@/constants/routes";
import { AppColors } from "@/styles/colors";
import { Flex, useDisclosure } from "@chakra-ui/react";
import { lazy, Suspense, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { BackupCheckDetail as BackupCheckDetailEntity, PrintCheckRequest } from "../schema/backup-checks.schema";
import { BackupCheckDetailLegend } from "@/components/common/section-components/requests/backup-check/BackupCheckDetailLegend";
import { BackupCheckMessageList } from "@/components/common/section-components/requests/backup-check/BackupCheckMessageList";
import { FieldValues } from "react-hook-form";
import { RequestTypes } from "@/constants/enums";
import { useRoles } from "@/hooks/useRoles";
import {
  cancelBackupCheckHandler,
  fetchBackupCheckDetailHandler,
  updateBackupCheckHandler,
  finalizeBackupCheckRequestHandler,
  printBackupCheckHandler,
} from "../schema/handler-functions";
import { toaster } from "@/components/ui/toaster";
import { BackupCheckStatus } from "@/constants/entity-status";
import dayjs from "dayjs";

export const BackupCheckDetail = () => {
  const [isActive, setIsActive] = useState<boolean>(false);

  useEffect(() => {
    setIsActive(true);
  }, []);

  const { id } = useParams();
  const navigate = useNavigate();

  const handleBack = () => {
    navigate(AppRoutes.BACKUP_CHECKS);
  };

  const [loadingBackupCheck, setLoadingBackupCheck] = useState<boolean>(false);
  const [backupCheck, setBackupCheck] = useState<
    BackupCheckDetailEntity | null | undefined
  >();

  const { open: openPrintModal, setOpen: setOpenPrintModal } = useDisclosure();
  const { open: openCancelModal, setOpen: setOpenCancelModal } =
    useDisclosure();

  const [loadingCancel, setLoadingCancel] = useState(false);

  const [printStep, setPrintStep] = useState<"printing" | "cheque_number">(
    "printing",
  );
  const [loadingPrint, setLoadingPrint] = useState(false);
  const [loadingFinalize, setLoadingFinalize] = useState(false);
  const [loadingFinalizeRequest, setLoadingFinalizeRequest] = useState(false);
  const [chequeNumber, setChequeNumber] = useState("");

  const { fetchPermissions } = useRoles();

  const fetchBackupCheck = async (showLoad: boolean = true) => {
    await fetchBackupCheckDetailHandler({
      id,
      setBackupCheck,
      setLoadingBackupCheck,
      showLoad,
      fetchPermissions,
      navigate,
    });
  };

  useEffect(() => {
    if (isActive && id) fetchBackupCheck();
  }, [isActive, id]);

  const handleOpenPrint = () => {
    setChequeNumber("");
    setPrintStep("printing");
    setOpenPrintModal(true);
  };

  const handleOpenEditNumber = () => {
    setChequeNumber("");
    setPrintStep("cheque_number");
    setOpenPrintModal(true);
  };

  const handleOpenCancel = () => {
    setOpenCancelModal(true);
  };

  const handleCancelBackupCheck = async (event?: FieldValues) => {
    try {
      setLoadingCancel(true);
      await cancelBackupCheckHandler({
        id: Number(id),
        event,
        navigate,
        fetchPermissions,
        onSuccess: async () => {
          setOpenCancelModal(false);
          await fetchBackupCheck(false);
        },
      });
    } finally {
      setLoadingCancel(false);
    }
  };

  const handleFinalizeRequest = async () => {
    try {
      setLoadingFinalizeRequest(true);
      await finalizeBackupCheckRequestHandler({
        id: Number(id),
        navigate,
        fetchPermissions,
        onSuccess: async () => {
          await fetchBackupCheck(false);
        },
      });
    } finally {
      setLoadingFinalizeRequest(false);
    }
  };

  const handlePrint = async () => {
    try {
      setLoadingPrint(true);
      const printRequest: PrintCheckRequest = {
        date: dayjs().format("YYYY-MM-DD"),
        datePayment: dayjs(backupCheck?.fechaTransferencia).format("YYYY-MM-DD"),
        payeeName: backupCheck?.destino?.titular || "",
        amount: backupCheck?.montoTransferido || 0,
      };

      await printBackupCheckHandler({
        printRequest,
        navigate,
        fetchPermissions,
        setOpenPrint: setOpenPrintModal,
        onSuccess: () => {
          setPrintStep("cheque_number");
          toaster.create({ 
            title: "Cheque impreso correctamente.",
            type: "success"
           });
        },
      });
    } finally {
      setLoadingPrint(false);
    }
  };

  const handleFinalize = async () => {
    try {
      setLoadingFinalize(true);
      await updateBackupCheckHandler({
        id: Number(id),
        chequeNumber,
        navigate,
        fetchPermissions,
        onSuccess: async () => {
          setOpenPrintModal(false);
          await fetchBackupCheck(false);
        },
      });
    } finally {
      setLoadingFinalize(false);
    }
  };

  useEffect(() => {
    if (openPrintModal && printStep === "printing") {
      handlePrint();
    }
  }, [openPrintModal, printStep]);

  return (
    <Flex
      flexDir="column"
      paddingTop="20px"
      gap="12px"
      height="100%"
      minHeight={0}
    >
      <SectionHeader
        title="Cheque respaldo"
        onBack={handleBack}
        backButton
        loading={loadingBackupCheck}
      />

      <Flex flex="1" minHeight={0} overflowY="auto" flexDir="column" gap="12px">
        {!loadingBackupCheck && (
          <>
            <BackupCheckAmountAndActions
              amount={backupCheck?.montoTransferido}
              status={backupCheck?.estado as any}
              handlePrint={handleOpenPrint}
              handleCancel={handleOpenCancel}
              handleOpenEditNumber={handleOpenEditNumber}
              handleFinalize={handleFinalizeRequest}
              loadingPrint={loadingPrint}
              loadingFinalize={loadingFinalizeRequest}
            />

            <BackupCheckMainDataCard request={backupCheck} />

            <Flex
              flexDir="column"
              padding="12px"
              border={`1px solid ${AppColors.WHITE_BORDER_1}`}
              borderRadius="12px"
              gap="25px"
            >
              <BackupCheckDetailLegend />
              <BackupCheckMessageList request={backupCheck} />
            </Flex>
          </>
        )}
      </Flex>

      {id && (
        <Suspense>
          <PrintBackupCheckModalDynamic
            open={openPrintModal}
            setOpen={setOpenPrintModal}
            step={printStep}
            chequeNumber={chequeNumber}
            setChequeNumber={setChequeNumber}
            loadingPrint={loadingPrint}
            loadingSave={loadingFinalize}
            onReprint={handlePrint}
            onAccept={handleFinalize}
            noReprint={backupCheck?.estado === BackupCheckStatus.PENDING_ENDOSE}
          />

          <RejectRequestModalDynamic
            open={openCancelModal}
            setOpen={setOpenCancelModal}
            handleAccept={handleCancelBackupCheck}
            loadingAccept={loadingCancel}
            isCancelling
            entityName="cheque respaldo"
            entityArticle="el"
            idTipoSolicitud={RequestTypes.ChequeRespaldo}

          />
        </Suspense>
      )}
    </Flex>
  );
};

const PrintBackupCheckModalDynamic = lazy(() =>
  import("@/components/modal/PrintBackupCheckModal").then((el) => ({
    default: el["PrintBackupCheckModal"],
  })),
);

const RejectRequestModalDynamic = lazy(() =>
  import("@/components/modal/RejectRequestModal").then((el) => ({
    default: el["RejectRequestModal"],
  })),
);